import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Sparkles, TrendingUp, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const tradingResponses: Record<string, string> = {
  'beli': 'Untuk keputusan beli, perhatikan beberapa hal: 1) Pastikan P/E ratio tidak terlalu tinggi (idealnya < 15), 2) ROE di atas 15%, 3) DER di bawah 1, 4) Volume transaksi meningkat. Gunakan strategi Dollar Cost Averaging untuk mengurangi risiko timing.',
  'jual': 'Sinyal jual biasanya muncul ketika: 1) Target profit sudah tercapai, 2) Fundamental perusahaan memburuk, 3) P/E ratio terlalu tinggi dibanding peers, 4) Ada berita negatif signifikan. Tetapkan stop loss di 5-10% dari harga beli.',
  'ihsg': 'IHSG adalah indeks utama BEI yang mencerminkan pergerakan seluruh saham. Saat IHSG bullish, sebagian besar saham cenderung naik. Perhatikan level support dan resistance IHSG untuk timing entry/exit.',
  'saham': 'Untuk pemula, disarankan mulai dari saham blue chip seperti BBCA, BBRI, TLKM yang memiliki fundamental kuat dan likuiditas tinggi. Diversifikasi ke 5-10 saham dari sektor berbeda.',
  'analisis': 'Ada 2 jenis analisis: 1) Fundamental - melihat kesehatan keuangan perusahaan (PER, PBV, ROE, DER), 2) Teknikal - melihat pola harga dan volume. Kombinasikan keduanya untuk hasil optimal.',
  'risiko': 'Manajemen risiko penting: 1) Jangan investasikan lebih dari 5% portfolio di satu saham, 2) Set stop loss, 3) Diversifikasi sektor, 4) Sisakan cash untuk averaging down, 5) Jangan panik saat market turun.',
  'pemula': 'Tips untuk pemula: 1) Mulai dengan dana yang siap hilang, 2) Pelajari analisis fundamental, 3) Buka RDN di sekuritas terpercaya, 4) Mulai dari saham LQ45, 5) Investasi berkala setiap bulan.',
  'dividen': 'Saham dividen cocok untuk passive income. Pilih saham dengan dividend yield > 3%, payout ratio stabil, dan track record dividen konsisten. Contoh: BBCA, TLKM, UNVR.',
  'default': 'Saya adalah asisten trading GOTRADINGZY. Saya bisa membantu Anda dengan analisis saham, strategi trading, manajemen risiko, dan tips investasi. Silakan tanyakan seputar trading saham Indonesia!'
};

export const ConsultChat = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Halo! Saya asisten trading GOTRADINGZY. Saya siap membantu Anda dengan analisis saham, strategi trading, dan tips investasi. Ada yang bisa saya bantu?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const generateResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    for (const [keyword, response] of Object.entries(tradingResponses)) {
      if (keyword !== 'default' && lowerMessage.includes(keyword)) {
        return response;
      }
    }
    return tradingResponses.default;
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const response = generateResponse(input);
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const quickQuestions = [
    'Kapan waktu yang tepat untuk beli?',
    'Bagaimana analisis saham pemula?',
    'Tips manajemen risiko trading',
    'Saham dividen terbaik?'
  ];

  if (!user) {
    return (
      <div className="card p-12 text-center">
        <Bot className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Trading Consultant</h3>
        <p className="text-slate-400">Silakan login untuk menggunakan fitur konsultasi trading</p>
      </div>
    );
  }

  return (
    <div className="card h-[600px] flex flex-col">
      <div className="p-4 border-b border-slate-800 flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full flex items-center justify-center">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-white">Trading Consultant</h3>
          <div className="flex items-center space-x-1 text-xs text-emerald-400">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
            <span>Online</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${message.role === 'user' ? 'order-2' : 'order-1'}`}>
              <div className={`flex items-start space-x-2 ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' ? 'bg-emerald-500' : 'bg-slate-700'
                }`}>
                  {message.role === 'user' ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-emerald-400" />}
                </div>
                <div className={`px-4 py-3 rounded-2xl ${
                  message.role === 'user'
                    ? 'bg-emerald-500 text-white rounded-br-md'
                    : 'bg-slate-800 text-slate-200 rounded-bl-md'
                }`}>
                  <p className="text-sm leading-relaxed">{message.content}</p>
                </div>
              </div>
              <p className={`text-xs text-slate-600 mt-1 ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                {message.timestamp.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex items-start space-x-2">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
              <Bot className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="bg-slate-800 px-4 py-3 rounded-2xl rounded-bl-md">
              <div className="flex space-x-1">
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {messages.length === 1 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => setInput(q)}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded-full transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 border-t border-slate-800">
        <div className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Tanyakan seputar trading saham..."
            className="input-field flex-1"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="btn-primary px-4 disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-slate-600 mt-2 flex items-center space-x-1">
          <AlertCircle className="w-3 h-3" />
          <span>Disclaimer: Bukan merupakan rekomendasi investasi</span>
        </p>
      </div>
    </div>
  );
};
